import { WidgetContainer } from '@/components/analytics/WidgetContainer';
import type { SessionActivity } from '@/types/analytics';
import styles from './TotalStudyTime.module.scss';

export interface TotalStudyTimeProps {
  data: SessionActivity | null;
  isLoading?: boolean;
  error?: Error | null;
}

/**
 * Total Study Time Widget
 *
 * Displays total study time with breakdown into hours, minutes, and seconds.
 * Shows average session duration and total sessions.
 */
export function TotalStudyTime({ data, isLoading, error }: TotalStudyTimeProps) {
  const isEmpty = !data || data.totalStudyTimeSeconds === 0;

  // Convert seconds to hours, minutes, seconds
  const formatTime = (totalSeconds: number) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return { hours, minutes, seconds };
  };

  const totalTime = data ? formatTime(data.totalStudyTimeSeconds) : { hours: 0, minutes: 0, seconds: 0 };
  const avgSessionTime = data && data.totalSessions > 0
    ? formatTime(Math.floor(data.totalStudyTimeSeconds / data.totalSessions))
    : { hours: 0, minutes: 0, seconds: 0 };

  return (
    <WidgetContainer
      title="Total Study Time"
      description="Time spent practicing"
      isLoading={isLoading}
      error={error}
      isEmpty={isEmpty}
      emptyMessage="Start practicing to track your study time"
      size="medium"
    >
      <div className={styles.studyTime}>
        {/* Time Breakdown */}
        <div className={styles.studyTime__breakdown}>
          <div className={styles.studyTime__unit}>
            <div className={styles.studyTime__unitValue}>{totalTime.hours}</div>
            <div className={styles.studyTime__unitLabel}>Hours</div>
          </div>
          <div className={styles.studyTime__separator}>:</div>
          <div className={styles.studyTime__unit}>
            <div className={styles.studyTime__unitValue}>
              {totalTime.minutes.toString().padStart(2, '0')}
            </div>
            <div className={styles.studyTime__unitLabel}>Minutes</div>
          </div>
          <div className={styles.studyTime__separator}>:</div>
          <div className={styles.studyTime__unit}>
            <div className={styles.studyTime__unitValue}>
              {totalTime.seconds.toString().padStart(2, '0')}
            </div>
            <div className={styles.studyTime__unitLabel}>Seconds</div>
          </div>
        </div>

        {/* Stats */}
        {data && (
          <div className={styles.studyTime__stats}>
            <div className={styles.studyTime__stat}>
              <span className={styles.studyTime__statLabel}>Avg Session</span>
              <span className={styles.studyTime__statValue}>
                {avgSessionTime.hours > 0
                  ? `${avgSessionTime.hours}h ${avgSessionTime.minutes}m`
                  : `${avgSessionTime.minutes}m ${avgSessionTime.seconds}s`}
              </span>
            </div>
            <div className={styles.studyTime__stat}>
              <span className={styles.studyTime__statLabel}>Total Sessions</span>
              <span className={styles.studyTime__statValue}>
                {data.totalSessions.toLocaleString()}
              </span>
            </div>
          </div>
        )}

        {/* Milestones */}
        {data && data.totalStudyTimeSeconds >= 3600 && (
          <div className={styles.studyTime__milestone}>
            {data.totalStudyTimeSeconds >= 36000 && '🏆 10+ hours of dedication!'}
            {data.totalStudyTimeSeconds >= 18000 && data.totalStudyTimeSeconds < 36000 && '⭐ 5+ hours of practice!'}
            {data.totalStudyTimeSeconds >= 3600 && data.totalStudyTimeSeconds < 18000 && '🎯 1+ hour milestone reached!'}
          </div>
        )}
      </div>
    </WidgetContainer>
  );
}
